import React, { useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  ExpandableSection,
  SpaceBetween,
  Spinner,
  StatusIndicator,
} from "@amzn/awsui-components-react";

/**
 * Renders the "required when triggered" DATE columns for a service, grouped by
 * the Task Manager dashboards its app belongs to. One ExpandableSection per
 * dashboard, each listing its DATE columns as checkboxes.
 *
 * The data is fetched by the parent page (in the background, while file
 * validation runs) and passed in via `dashboards`; this component is purely
 * presentational so the fetch can start before the component renders.
 *
 * Props:
 *   dashboards: [ { dashboard_name, dashboard_id, date_columns: [name] } ] | null
 *   loading:    boolean  - fetch in flight
 *   error:      string   - fetch error message (optional)
 *   onColumnToggle: (info) => void  - called when a checkbox changes; defaults
 *                   to a console.log (per current requirement).
 */
const RequiredDateColumns = ({
  dashboards,
  loading = false,
  error = "",
  onColumnToggle,
  onSelectionChange,
}) => {
  // Track checked state keyed by `${dashboard_id}::${columnName}`.
  const [checked, setChecked] = useState({});

  // Reduce the flat checked-map into the transaction-record shape:
  //   { "<dashboard_id>": { dates: [columnName, ...] }, ... }
  // (only dashboards with >=1 pick). Keyed by dashboard id rather than an array.
  const toSelection = (checkedMap) => {
    const byDashboard = {};
    Object.entries(checkedMap).forEach(([key, isChecked]) => {
      if (!isChecked) return;
      const sep = key.indexOf("::");
      if (sep < 0) return;
      const dashboardId = key.slice(0, sep);
      const columnName = key.slice(sep + 2);
      if (!byDashboard[dashboardId]) byDashboard[dashboardId] = { dates: [] };
      byDashboard[dashboardId].dates.push(columnName);
    });
    return byDashboard;
  };

  const handleToggle = (dashboard, columnName, isChecked) => {
    const key = `${dashboard.dashboard_id}::${columnName}`;
    // Compute the next map outside the updater so the setState updater stays pure
    // (no side effects during render/commit). Emit the selection to the parent here.
    const next = { ...checked, [key]: isChecked };
    setChecked(next);
    if (typeof onSelectionChange === "function") {
      onSelectionChange(toSelection(next));
    }
    const info = {
      dashboard_id: dashboard.dashboard_id,
      dashboard_name: dashboard.dashboard_name,
      column: columnName,
      checked: isChecked,
    };
    if (typeof onColumnToggle === "function") {
      onColumnToggle(info);
    } else {
      console.log("[RequiredDateColumns] selection changed:", info);
    }
  };

  // Only show dashboards that actually have required DATE columns.
  const dashboardsWithColumns = useMemo(
    () =>
      (Array.isArray(dashboards) ? dashboards : []).filter(
        (d) => Array.isArray(d?.date_columns) && d.date_columns.length > 0
      ),
    [dashboards]
  );

  if (loading) {
    return (
      <Box padding={{ top: "xs" }}>
        <Spinner size="normal" /> Loading optional inputs...
      </Box>
    );
  }

  if (error) {
    return (
      <Box padding={{ top: "xs" }}>
        <StatusIndicator type="warning">
          Could not load optional inputs: {error}
        </StatusIndicator>
      </Box>
    );
  }

  if (dashboardsWithColumns.length === 0) {
    // Before a fetch has run (dashboards === null) render nothing. After a fetch
    // that returned no required DATE columns, surface a hint instead of silently
    // rendering nothing, so it's clear the section loaded but had no data.
    if (dashboards == null) return null;
    return (
      <Box padding={{ top: "xs" }} color="text-status-inactive">
        No optional inputs for this deliverable.
      </Box>
    );
  }

  return (
    <SpaceBetween size="xs">
      <Box variant="h5">Optional inputs</Box>
      {dashboardsWithColumns.map((dashboard) => (
        <ExpandableSection
          key={dashboard.dashboard_id}
          defaultExpanded
          headerText={dashboard.dashboard_name || `Dashboard ${dashboard.dashboard_id}`}
        >
          <SpaceBetween size="xxs">
            {dashboard.date_columns.map((columnName) => {
              const key = `${dashboard.dashboard_id}::${columnName}`;
              return (
                <Checkbox
                  key={key}
                  checked={!!checked[key]}
                  onChange={({ detail }) =>
                    handleToggle(dashboard, columnName, detail.checked)
                  }
                >
                  {columnName}
                </Checkbox>
              );
            })}
          </SpaceBetween>
        </ExpandableSection>
      ))}
    </SpaceBetween>
  );
};

export default RequiredDateColumns;
